import { api } from './auth'
import type { Maintenance, MaintenanceCreate } from '@/types/maintenance'

type MaintenanceListResponse = { results: Maintenance[]; count: number }

export const maintenanceApi = {
  /**
   * Fetch maintenance list. The backend may return different shapes (array or paginated object).
   * Normalize to { results: Maintenance[]; count: number }.
   */
  async getAll(params?: Record<string, unknown>): Promise<MaintenanceListResponse> {
    const response = await api.get('equipment/maintenance/', { params })
    const data: unknown = response.data

    // If API returns an array directly
    if (Array.isArray(data)) {
      return { results: data as Maintenance[], count: data.length }
    }

    // If API returns an object, attempt to extract common keys without using `any`.
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>

      if (Array.isArray(obj.results)) {
        const res = obj.results as unknown
        const resultsArray = Array.isArray(res) ? (res as Maintenance[]) : []
        return {
          results: resultsArray,
          count: typeof obj.count === 'number' ? obj.count : resultsArray.length,
        }
      }

      if (Array.isArray(obj.data)) {
        return { results: obj.data as Maintenance[], count: (obj.data as Maintenance[]).length }
      }

      if (Array.isArray(obj.items)) {
        return { results: obj.items as Maintenance[], count: (obj.items as Maintenance[]).length }
      }

      // Fallback: find the first array property on the object
      for (const key of Object.keys(obj)) {
        const val = obj[key]
        if (Array.isArray(val)) {
          return { results: val as Maintenance[], count: (val as Maintenance[]).length }
        }
      }
    }

    // Nothing recognizable: return empty list
    return { results: [], count: 0 }
  },

  async getById(id: number): Promise<Maintenance> {
    const response = await api.get(`equipment/maintenance/${id}/`)
    return response.data as Maintenance
  },

  async create(data: Partial<Maintenance> | Record<string, unknown>): Promise<Maintenance> {
    // Defensive normalization to match backend MaintenanceCreate shape
    const raw = data as Record<string, unknown>

    // Equipment id: accept equipment_id or equipment
    const equipmentIdRaw = raw.equipment_id ?? raw.equipment ?? raw.equipmentId
    const equipment_id = Number(equipmentIdRaw) || 0

    // Maintenance type: restrict to allowed values
    const mt = typeof raw.maintenance_type === 'string' ? raw.maintenance_type : ''
    const allowed = ['scheduled', 'repair', 'inspection'] as const
    const maintenance_type = (allowed as readonly string[]).includes(mt)
      ? (mt as 'scheduled' | 'repair' | 'inspection')
      : 'scheduled'

    const description = typeof raw.description === 'string' ? raw.description : ''

    // Cost: coerce to number if possible
    const costVal = raw.cost ?? raw.amount
    const cost = costVal === undefined || costVal === null ? undefined : Number(costVal)

    // Dates: accept maintenance_date or date, ensure ISO yyyy-mm-dd
    const mDateRaw = raw.maintenance_date ?? raw.date
    let maintenance_date = undefined as string | undefined
    if (typeof mDateRaw === 'string' && mDateRaw.trim() !== '') {
      maintenance_date = mDateRaw
    } else if (mDateRaw instanceof Date && !Number.isNaN(mDateRaw.getTime())) {
      maintenance_date = mDateRaw.toISOString().split('T')[0]
    }

    const nextRaw = raw.next_due_date ?? raw.next_maintenance_date ?? raw.nextDueDate
    let next_due_date = undefined as string | undefined
    if (typeof nextRaw === 'string' && nextRaw.trim() !== '') {
      next_due_date = nextRaw
    } else if (nextRaw instanceof Date && !Number.isNaN(nextRaw.getTime())) {
      next_due_date = nextRaw.toISOString().split('T')[0]
    }

    const technician = typeof raw.technician === 'string' ? raw.technician : undefined
    const notes = typeof raw.notes === 'string' ? raw.notes : undefined

    // Ensure maintenance_date is present (backend expects a string date)
    const maintenance_date_str: string = maintenance_date ?? new Date().toISOString().split('T')[0]

    const payload: MaintenanceCreate = {
      equipment_id,
      maintenance_type,
      description,
      maintenance_date: maintenance_date_str,
    }

    if (Number.isFinite(cost as number)) payload.cost = cost as number
    if (next_due_date) payload.next_due_date = next_due_date
    if (technician) payload.technician = technician
    if (notes) payload.notes = notes

    // Also include common alternate field names to tolerate backend variations
    const postPayload: Record<string, unknown> = {
      ...payload,
      // alternate names
      equipment: payload.equipment_id,
      equipmentId: payload.equipment_id,
      next_maintenance_date: payload.next_due_date ?? null,
      nextDueDate: payload.next_due_date ?? null,
      // send cost both as number (if present) and as string
      ...(payload.cost !== undefined ? { cost_string: String(payload.cost) } : {}),
    }

    const response = await api.post('equipment/maintenance/', postPayload)
    return response.data as Maintenance
  },

  async update(id: number, data: Partial<Maintenance>): Promise<Maintenance> {
    const response = await api.patch(`equipment/maintenance/${id}/`, data)
    return response.data as Maintenance
  },

  async delete(id: number): Promise<void> {
    await api.delete(`equipment/maintenance/${id}/`)
  },
}
